/**
 * Order returns flow for Order Tracker V2 (loaded before tracker-v2.js).
 * Inline selection on shipment cards; drawer for Reason/Condition pickers and Report All.
 */
(function (global) {
    const BUCKET = 'despatched';
    const CHANGED_MIND_ISSUE_ID = 6;

    let returnsEditModeActive = false;
    let returnsHandlersBound = false;

    const returnsItemRegistry = new Map();
    const returnsShipmentRegistry = new Map();

    let returnsFlowState = createReturnsFlowState();

    function createEmptyDrawerState() {
        return {
            context: null,
            lineKey: null,
            batchIndex: null,
            pendingIssueId: null,
            pendingProductStatusId: null,
            reportAllStep: null,
        };
    }

    function createReturnsFlowState() {
        return {
            step: null,
            lineSelections: new Map(),
            drawer: createEmptyDrawerState(),
            lastRmaNumber: null,
            lastTicketId: null,
        };
    }

    function t() {
        return global.orderTrackerTranslations || {};
    }

    function toApiLineId(raw) {
        const s = String(raw ?? '').trim();
        if (!s) return '';
        return s.slice(s.lastIndexOf('-') + 1);
    }

    function getRoot() {
        return document.getElementById('order-tracking');
    }

    function getOrderContext() {
        return global.__orderTrackerCachedOrderData || null;
    }

    function getOrderTrackerDrawer() {
        return document.querySelector('order-tracker-drawer');
    }

    function getReturnsStepEl() {
        return document.getElementById('order-returns-step');
    }

    function getShipmentsContainer() {
        return document.getElementById('order_items_container');
    }

    function getReturnsFooterEl() {
        return document.getElementById('order-returns-footer');
    }

    function escapeHtml(value) {
        if (value == null) return '';
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    function escapeHtmlAttr(value) {
        return escapeHtml(value).replace(/'/g, '&#39;');
    }

    function decodeHtmlEntities(value) {
        if (value == null) return '';
        const textarea = document.createElement('textarea');
        textarea.innerHTML = String(value);
        return textarea.value;
    }

    function formatMoney(val) {
        if (typeof global.__orderTrackerFormatMoney === 'function') {
            return global.__orderTrackerFormatMoney(val);
        }
        const n = parseFloat(val);
        return Number.isFinite(n) ? n.toFixed(2) : '0.00';
    }

    function parseNumber(value) {
        const n = parseFloat(value);
        return Number.isFinite(n) ? n : 0;
    }

    function computeReturnLineGrossAmount(entry, quantity) {
        const maxQty = Math.max(0, parseInt(entry.item.quantity, 10) || 0);
        const lineGross = parseNumber(entry.item.grossAmount);
        if (maxQty <= 0 || quantity <= 0) return 0;
        return Math.round((lineGross / maxQty) * quantity * 100) / 100;
    }

    function sanitizeHref(url) {
        const s = (url || '').trim();
        if (!s.toLowerCase().startsWith('https://')) return '';
        return s;
    }

    function setVisiblyHidden(el, hidden) {
        if (!el) return;
        el.classList.toggle('hidden', hidden);
        if (hidden) el.setAttribute('hidden', '');
        else el.removeAttribute('hidden');
    }

    function setOrderHeaderHidden(hidden) {
        setVisiblyHidden(getRoot()?.querySelector('.order-header'), hidden);
    }

    function getReturnsFlowConfig() {
        const el = getRoot();
        const parseIntSetting = (raw, fallback) => {
            const n = parseInt(raw, 10);
            return Number.isFinite(n) ? n : fallback;
        };
        const url = (raw) => {
            const s = (raw || '').trim();
            return s.toLowerCase().startsWith('https://') ? s : '';
        };
        const cancellationSupport = url(el?.dataset.cancellationSupportUrl);
        return {
            maxThresholdDays: parseIntSetting(el?.dataset.returnsMaxThresholdDays, 14),
            supportUrl: url(el?.dataset.returnsSupportUrl) || cancellationSupport,
            warehouseAddress: (el?.dataset.returnsWarehouseAddress || '').trim(),
        };
    }

    function getApiCredentials() {
        const apiKey = document.querySelector('#apiKeyContainer')?.dataset.apiKey;
        const order = getOrderContext();
        const customer = order?.customerInformation || {};
        const customerName =
            (customer.fullName || '').trim() ||
            [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim();

        return {
            clientApiKey: apiKey,
            orderNumber: order?.orderSummary?.orderNo,
            customerEmail: customer.email || '',
            customerName,
            shopifyOrderInternalId: order?.orderSummary?.shopifyOrderInternalId ?? null,
        };
    }

    function buildCreatedBy(creds) {
        const name = creds.customerName || 'Customer';
        const email = creds.customerEmail || '';
        return email ? `Order Status - ${name} <${email}>` : `Order Status - ${name}`;
    }

    function getReturnsApiUrl() {
        const raw = (getRoot()?.dataset.orderAmendApiUrl || '').trim();
        const apiKey = getApiCredentials().clientApiKey;
        if (!raw) return '';
        if (apiKey) {
            try {
                const url = new URL(raw);
                url.searchParams.set('clientApiKey', apiKey);
                return url.toString();
            } catch {
                return raw;
            }
        }
        return raw;
    }

    function makeLineKey(batchIndex, itemIndex) {
        return `${BUCKET}:${batchIndex}:${itemIndex}`;
    }

    function getLineContainer(lineKey) {
        return getRoot()?.querySelector(
            `[data-oa-return-line-key="${CSS.escape(lineKey)}"]`
        );
    }

    function startOfToday() {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }

    function daysSinceFulfilledDate(shipmentOrBatch) {
        const fulfilledDate = shipmentOrBatch?.date;
        if (!fulfilledDate || isNaN(new Date(fulfilledDate).getTime())) return Infinity;
        const today = startOfToday();
        const d = new Date(fulfilledDate);
        d.setHours(0, 0, 0, 0);
        return Math.max(0, Math.floor((today.getTime() - d.getTime()) / 86400000));
    }

    function isShipmentEligibleForReturn(shipmentOrBatch) {
        return daysSinceFulfilledDate(shipmentOrBatch) <= getReturnsFlowConfig().maxThresholdDays;
    }

    function getReturnedQuantityFromReturn(ret) {
        const parent = parseInt(ret?.parentItemQuantity, 10);
        if (Number.isFinite(parent) && parent > 0) return parent;
        if (Array.isArray(ret?.items)) {
            return ret.items.reduce((sum, it) => sum + (parseInt(it.quantity, 10) || 0), 0);
        }
        return parseInt(ret?.quantity ?? ret?.returnQuantity, 10) || 0;
    }

    function getReturnedQuantity(item) {
        return (item?.returns || []).reduce((t, r) => t + getReturnedQuantityFromReturn(r), 0);
    }

    function getReturnableQuantity(item) {
        const ordered = parseInt(item?.quantity, 10) || 0;
        return Math.max(0, ordered - getReturnedQuantity(item));
    }

    function isLineEligibleForReturn(item, shipmentOrBatch) {
        return isShipmentEligibleForReturn(shipmentOrBatch) && getReturnableQuantity(item) > 0;
    }

    function shipmentHasReturnableItems(shipmentOrBatch) {
        return (shipmentOrBatch?.items || []).some((item) => getReturnableQuantity(item) > 0);
    }

    function getAllIssueOptions() {
        return (t().returnIssueOptions || []).map((opt) => ({
            ...opt,
            label: decodeHtmlEntities(opt.label),
        }));
    }

    function getProductStatusOptions() {
        return (t().returnProductStatusOptions || []).map((opt) => ({
            ...opt,
            label: decodeHtmlEntities(opt.label),
        }));
    }

    function sortIssueOptions(options) {
        return [...(options || [])].sort(
            (a, b) => (Number(a.order) || 0) - (Number(b.order) || 0)
        );
    }

    function getIssueOptionsForLine() {
        return sortIssueOptions(getAllIssueOptions());
    }

    function getIssueLabel(issueId) {
        const opt = getAllIssueOptions().find((o) => Number(o.id) === Number(issueId));
        return opt?.label || "I've changed my mind";
    }

    function getProductStatusLabel(productStatusId) {
        const opt = getProductStatusOptions().find(
            (o) => Number(o.id) === Number(productStatusId)
        );
        return opt?.label || '';
    }

    function initLineSelectionsForAllShipments() {
        const selections = new Map();
        returnsItemRegistry.forEach((_, lineKey) => {
            selections.set(lineKey, { quantity: 0, issueId: null, productStatusId: null });
        });
        returnsFlowState.lineSelections = selections;
    }

    function hasPositiveLineSelection() {
        for (const { quantity } of returnsFlowState.lineSelections.values()) {
            if (quantity > 0) return true;
        }
        return false;
    }

    function updateReportContinueButton() {
        const btn = getRoot()?.querySelector('[data-oa-return-action="report-continue"]');
        if (!btn) return;
        btn.disabled = !hasPositiveLineSelection();
    }

    function hasAnyReturnSelections() {
        for (const sel of returnsFlowState.lineSelections.values()) {
            if (sel.quantity > 0 || sel.issueId != null || sel.productStatusId != null) {
                return true;
            }
        }
        return false;
    }

    function validateReturnSelections() {
        const invalidLines = [];
        let hasAnyQty = false;

        returnsFlowState.lineSelections.forEach((sel, lineKey) => {
            if (sel.quantity <= 0) return;
            hasAnyQty = true;

            if (sel.productStatusId == null || !sel.productStatusId) {
                invalidLines.push({ lineKey, missing: ['condition'] });
            }
        });

        return {
            valid: invalidLines.length === 0 && hasAnyQty,
            invalidLines,
            hasAnyQty,
        };
    }

    function buildLineValidationMessage(missingFields) {
        const tr = t();
        const parts = [];
        if (missingFields.includes('condition')) {
            parts.push(tr.returnChangesIncompleteConditionRequired || 'Select a condition');
        }
        const prefix = tr.returnChangesIncomplete || 'Changes incomplete';
        return parts.length ? `${prefix}: ${parts.join(', ')}` : prefix;
    }

    function showLineValidationError(lineKey, message) {
        const container = getLineContainer(lineKey);
        const banner = container?.querySelector('[data-oa-return-item-error]');
        if (!banner) return;
        const textEl = banner.querySelector('.order-returns__item-error-text');
        if (textEl) textEl.textContent = message;
        setVisiblyHidden(banner, false);
    }

    function clearLineValidationError(lineKey) {
        const container = getLineContainer(lineKey);
        const banner = container?.querySelector('[data-oa-return-item-error]');
        if (banner) setVisiblyHidden(banner, true);
    }

    function clearAllReturnValidationErrors() {
        getRoot()?.querySelectorAll('[data-oa-return-item-error]').forEach((el) => {
            setVisiblyHidden(el, true);
        });
    }

    function getSelectedReturnItems() {
        const items = [];
        returnsFlowState.lineSelections.forEach((sel, lineKey) => {
            if (sel.quantity <= 0) return;
            const entry = returnsItemRegistry.get(lineKey);
            if (entry) {
                items.push({
                    lineKey,
                    entry,
                    quantity: sel.quantity,
                    issueId: sel.issueId,
                    productStatusId: sel.productStatusId,
                    itemNumber: String(entry.item.itemNumber ?? ''),
                    grossAmount: computeReturnLineGrossAmount(entry, sel.quantity),
                });
            }
        });
        return items;
    }

    function selectionRequiresSupportRedirect() {
        return getSelectedReturnItems().some(
            (item) => item.issueId != null && Number(item.issueId) !== CHANGED_MIND_ISSUE_ID
        );
    }

    function isValidDrawerLinkHref(href) {
        return (href || '').trim().toLowerCase().startsWith('https://');
    }

    function getDrawerButtonClass(b) {
        if (b.primary) return 'button--primary';
        return 'button--outline';
    }

    function renderActionButton(b, { actionAttr = 'data-oa-return-action' } = {}) {
        const classes = `button ${getDrawerButtonClass(b)}`;
        const label = escapeHtml(b.label || '');
        const href = (b.href || '').trim();

        if (isValidDrawerLinkHref(href)) {
            const target = b.target === '_blank' ? '_blank' : '_self';
            const targetAttr =
                target === '_blank' ? ' target="_blank" rel="noopener noreferrer"' : '';
            return `<a href="${escapeHtmlAttr(href)}" class="${classes}"${targetAttr}>${label}</a>`;
        }

        const action = b.action ? `${actionAttr}="${escapeHtmlAttr(b.action)}"` : '';
        const disabled = b.disabled ? ' disabled' : '';
        const hidden = b.hidden ? ' hidden' : '';
        return `<button type="button" class="${classes}" ${action}${disabled}${hidden}>${label}</button>`;
    }

    function buildContactSupportButton(overrides = {}) {
        const tr = t();
        return {
            action: 'contact-support',
            label: tr.returnContactSupport || tr.orderAmendsCancellationContactSupport || 'Contact Support',
            href: getReturnsFlowConfig().supportUrl,
            target: '_self',
            ...overrides,
        };
    }

    function returnFooterHtml(buttons) {
        return `<div class="order-details__cancel-reason-footer button-group order-return-drawer__footer order-return-drawer__footer--stacked">${(buttons || [])
            .map((b) => renderActionButton(b))
            .join('')}</div>`;
    }

    function formatWarehouseAddressHtml(raw) {
        if (!raw) return '';
        return escapeHtml(raw).replace(/\n/g, '<br>');
    }

    function renderDrawerIntro(text) {
        if (!text) return '';
        return `<div class="order-tracker-drawer__sub-header">
            <p>${escapeHtml(text)}</p>
        </div>`;
    }

    function renderRadioFieldset(options, name, selectedId, legend) {
        const optionsHtml = (options || [])
            .map(
                (opt) => `
            <label class="order-details__cancel-reason-option">
                <input type="radio" class="order-details__cancel-reason-input" name="${escapeHtmlAttr(name)}" value="${escapeHtmlAttr(String(opt.id))}"${Number(selectedId) === Number(opt.id) ? ' checked' : ''} />
                <span class="order-details__cancel-reason-label">${opt.label}</span>
            </label>`
            )
            .join('');

        return `
            <fieldset class="order-details__cancel-reason-fieldset">
                <legend class="visually-hidden">${escapeHtml(legend || '')}</legend>
                ${optionsHtml}
            </fieldset>`;
    }

    function resetDrawerState() {
        returnsFlowState.drawer = createEmptyDrawerState();
    }

    function closeSelectionDrawer() {
        if (returnsEditModeActive) {
            returnsFlowState.step = 'selecting';
        }
        getOrderTrackerDrawer()?.close();
        resetDrawerState();
    }

    function openReturnDrawerContent(options = {}) {
        const drawer = getOrderTrackerDrawer();
        if (!drawer) return false;

        const { title = '', rootHtml = '', footerHtml: footer = null } = options;
        const html = `<div class="order-return-drawer-root">${rootHtml}</div>`;
        drawer.open({ title: title || '', html, footerHtml: footer });
        return true;
    }

    function getDrawerPrimaryButtonConfig() {
        const tr = t();
        const { context, pendingProductStatusId } = returnsFlowState.drawer;

        if (context === 'lineIssue' || context === 'reportAllIssue') {
            return {
                action: context === 'reportAllIssue' ? 'report-all-next' : 'drawer-continue',
                label: tr.returnContinue || 'Continue',
                primary: true,
                disabled: false,
            };
        }

        if (context === 'lineProductStatus' || context === 'reportAllProductStatus') {
            const hasSelection = pendingProductStatusId != null;
            return {
                action: context === 'reportAllProductStatus' ? 'report-all-continue' : 'drawer-continue',
                label: hasSelection
                    ? tr.returnContinue || 'Continue'
                    : tr.returnSelectProductStatus || 'Select a condition',
                primary: true,
                disabled: !hasSelection,
            };
        }

        return { action: 'drawer-continue', label: tr.returnContinue || 'Continue', primary: true, disabled: true };
    }

    function updateDrawerPrimaryButton() {
        const btn = getOrderTrackerDrawer()?.querySelector(
            '[data-oa-return-action="drawer-continue"], [data-oa-return-action="report-all-next"], [data-oa-return-action="report-all-continue"]'
        );
        if (!btn) return;
        const cfg = getDrawerPrimaryButtonConfig();
        btn.disabled = cfg.disabled;
        btn.textContent = cfg.label;
        btn.dataset.oaReturnAction = cfg.action;
    }

    function renderSelectionDrawer() {
        const tr = t();
        const drawer = returnsFlowState.drawer;
        let title = '';
        let rootHtml = '';
        let footerButtons = [];

        if (drawer.context === 'lineIssue') {
            const sel = returnsFlowState.lineSelections.get(drawer.lineKey);
            title = tr.returnSelectReason || 'Select a Reason (optional)';
            rootHtml =
                renderDrawerIntro(
                    tr.returnSelectReasonIntro ||
                        'Add an optional reason to help us understand what went wrong'
                ) +
                renderRadioFieldset(
                    getIssueOptionsForLine(),
                    'return-issue',
                    drawer.pendingIssueId ?? sel?.issueId ?? '',
                    tr.returnIssueLegend || 'Select a reason'
                );
            footerButtons = [
                getDrawerPrimaryButtonConfig(),
                { action: 'drawer-cancel', label: tr.returnCancel || 'Cancel' },
            ];
        } else if (drawer.context === 'lineProductStatus') {
            const sel = returnsFlowState.lineSelections.get(drawer.lineKey);
            title = tr.returnSelectProductStatus || 'Select a condition';
            rootHtml =
                renderDrawerIntro(
                    tr.returnSelectConditionIntro ||
                        'Select the condition of the products you want to return.'
                ) +
                renderRadioFieldset(
                    getProductStatusOptions(),
                    'return-product-status',
                    drawer.pendingProductStatusId ?? sel?.productStatusId ?? '',
                    tr.returnProductStatusLegend || 'Select a condition'
                );
            footerButtons = [
                getDrawerPrimaryButtonConfig(),
                { action: 'drawer-cancel', label: tr.returnCancel || 'Cancel' },
            ];
        } else if (drawer.context === 'reportAllIssue') {
            title = tr.returnSelectReason || 'Select a Reason (optional)';
            rootHtml =
                renderDrawerIntro(
                    tr.returnSelectReasonIntro ||
                        'Add an optional reason to help us understand what went wrong'
                ) +
                renderRadioFieldset(
                    getIssueOptionsForLine(),
                    'return-issue',
                    drawer.pendingIssueId ?? '',
                    tr.returnIssueLegend || 'Select a reason'
                );
            footerButtons = [
                getDrawerPrimaryButtonConfig(),
                { action: 'drawer-cancel', label: tr.returnCancel || 'Cancel' },
            ];
        } else if (drawer.context === 'reportAllProductStatus') {
            title = tr.returnSelectProductStatus || 'Select a condition';
            rootHtml =
                renderDrawerIntro(
                    tr.returnSelectConditionIntro ||
                        'Select the condition of the products you want to return.'
                ) +
                renderRadioFieldset(
                    getProductStatusOptions(),
                    'return-product-status',
                    drawer.pendingProductStatusId ?? '',
                    tr.returnProductStatusLegend || 'Select a condition'
                );
            footerButtons = [
                getDrawerPrimaryButtonConfig(),
                { action: 'report-all-back', label: tr.returnBack || 'Back' },
            ];
        }

        returnsFlowState.step = 'drawer';
        openReturnDrawerContent({
            title,
            rootHtml,
            footerHtml: returnFooterHtml(footerButtons),
        });
    }

    function openLineIssueDrawer(lineKey) {
        const sel = returnsFlowState.lineSelections.get(lineKey);
        returnsFlowState.drawer = {
            context: 'lineIssue',
            lineKey,
            batchIndex: null,
            pendingIssueId: sel?.issueId ?? null,
            pendingProductStatusId: null,
            reportAllStep: null,
        };
        renderSelectionDrawer();
    }

    function openLineProductStatusDrawer(lineKey) {
        const sel = returnsFlowState.lineSelections.get(lineKey);
        returnsFlowState.drawer = {
            context: 'lineProductStatus',
            lineKey,
            batchIndex: null,
            pendingIssueId: null,
            pendingProductStatusId: sel?.productStatusId ?? null,
            reportAllStep: null,
        };
        renderSelectionDrawer();
    }

    function openReportAllDrawer(batchIndex) {
        returnsFlowState.drawer = {
            context: 'reportAllIssue',
            lineKey: null,
            batchIndex,
            pendingIssueId: null,
            pendingProductStatusId: null,
            reportAllStep: 'issue',
        };
        renderSelectionDrawer();
    }

    function applyLineDrawerSelectionFromDrawer(drawer) {
        const { context, lineKey, pendingIssueId, pendingProductStatusId } = drawer;
        if (!lineKey) return;

        const existing = returnsFlowState.lineSelections.get(lineKey) || {
            quantity: 0,
            issueId: null,
            productStatusId: null,
        };

        if (context === 'lineIssue') {
            returnsFlowState.lineSelections.set(lineKey, {
                ...existing,
                issueId: pendingIssueId != null ? Number(pendingIssueId) : null,
            });
        } else if (context === 'lineProductStatus' && pendingProductStatusId != null) {
            returnsFlowState.lineSelections.set(lineKey, {
                ...existing,
                productStatusId: Number(pendingProductStatusId),
            });
        }
    }

    function applyLineDrawerSelection() {
        applyLineDrawerSelectionFromDrawer(returnsFlowState.drawer);
        const { lineKey } = returnsFlowState.drawer;
        if (lineKey) syncInlineLineUi(lineKey);
    }

    function applyReportAllSelections(issueId, productStatusId, batchIndex) {
        if (batchIndex == null) return;

        returnsItemRegistry.forEach((entry, lineKey) => {
            if (entry.batchIndex !== batchIndex) return;

            const maxQty = getReturnableQuantity(entry.item);
            if (maxQty <= 0) return;
            returnsFlowState.lineSelections.set(lineKey, {
                quantity: maxQty,
                issueId: issueId != null ? Number(issueId) : null,
                productStatusId: Number(productStatusId),
            });
        });
        syncInlineReturnsUi();
    }

    function finalizeDrawerOnClose(drawer) {
        if (!drawer?.context) return false;

        if (drawer.context === 'reportAllProductStatus') {
            ensureReturnsEditMode();
            if (drawer.batchIndex != null) {
                applyReportAllSelections(
                    drawer.pendingIssueId,
                    drawer.pendingProductStatusId,
                    drawer.batchIndex
                );
            }
            return true;
        }

        if (drawer.context === 'lineProductStatus') {
            ensureReturnsEditMode();
            if (drawer.pendingProductStatusId != null && drawer.lineKey) {
                applyLineDrawerSelectionFromDrawer(drawer);
                syncInlineLineUi(drawer.lineKey);
            }
            return true;
        }

        return false;
    }

    function ensureReturnsEditMode() {
        if (returnsEditModeActive) return;
        if (returnsShipmentRegistry.size === 0) return;

        returnsFlowState = createReturnsFlowState();
        initLineSelectionsForAllShipments();
        setReturnsEditMode(true);
        showSelectingStep();
    }

    function updateReturnQtyStepButtons(qtyWrap, qty, maxQty) {
        const minusBtn = qtyWrap?.querySelector('[data-oa-return-qty-step="-1"]');
        const plusBtn = qtyWrap?.querySelector('[data-oa-return-qty-step="1"]');
        if (minusBtn) minusBtn.disabled = qty <= 0;
        if (plusBtn) plusBtn.disabled = maxQty > 0 && qty >= maxQty;
    }

    function syncInlineLineUi(lineKey) {
        const container = getLineContainer(lineKey);
        if (!container) return;

        const tr = t();
        const sel = returnsFlowState.lineSelections.get(lineKey) || {
            quantity: 0,
            issueId: null,
            productStatusId: null,
        };
        const qty = sel.quantity || 0;
        const active = qty > 0;

        const addBtn = container.querySelector('[data-oa-return-add]');
        const qtyContainer = container.querySelector('.order-amends__returns-qty-container');
        const qtyWrap = container.querySelector('[data-oa-return-qty]');
        const qtyCurrent = container.querySelector('[data-oa-return-qty-current]');
        const detailCard = container.querySelector('[data-oa-return-detail-card]');
        const issueLabelEl = container.querySelector('[data-oa-return-issue-label]');
        const statusLabelEl = container.querySelector('[data-oa-return-product-status-label]');

        setVisiblyHidden(addBtn, active);
        // setVisiblyHidden(qtyWrap, !active);
        setVisiblyHidden(detailCard, !active);
        setVisiblyHidden(qtyContainer, !active);

        if (qtyCurrent) qtyCurrent.textContent = String(qty);

        if (qtyWrap) {
            const entry = returnsItemRegistry.get(lineKey);
            const maxQty =
                parseInt(qtyWrap.dataset.maxQty, 10) ||
                getReturnableQuantity(entry?.item) ||
                0;
            updateReturnQtyStepButtons(qtyWrap, qty, maxQty);
        }

        const reasonPlaceholder = tr.returnSelectReasonPlaceholder || 'Select a reason';
        const statusPlaceholder = tr.returnSelectProductStatus || 'Select a condition';

        if (issueLabelEl) {
            issueLabelEl.textContent =
                sel.issueId != null ? getIssueLabel(sel.issueId) : reasonPlaceholder;
        }

        if (statusLabelEl) {
            statusLabelEl.textContent =
            sel.productStatusId && sel.productStatusId != null
                    ? getProductStatusLabel(sel.productStatusId)
                    : statusPlaceholder;
        }

        if (sel.productStatusId != null || !active) {
            clearLineValidationError(lineKey);
        }

        updateReportContinueButton();
    }

    function syncInlineReturnsUi() {
        returnsItemRegistry.forEach((_, lineKey) => {
            syncInlineLineUi(lineKey);
        });
        updateReportContinueButton();
    }

    function renderReturnItemCardHtml(item, { quantity, issueId, productStatusId }) {
        const hasImage = item.itemImageUrl && item.itemImageUrl.trim() !== '';
        const safeImgSrc = hasImage ? sanitizeHref(item.itemImageUrl) : '';
        const imageHtml =
            hasImage && safeImgSrc
                ? `<img src="${escapeHtmlAttr(safeImgSrc)}" alt="${escapeHtmlAttr(item.itemDescription)}" class="shipment__item-image">`
                : `<div class="shipment__item-placeholder shipment__item-image"></div>`;

        const qtyLabel = (t().returnQtyLabel || 'Qty: {{ qty }}').replace(
            /\{\{\s*qty\s*\}\}/g,
            String(quantity)
        );

        const reasonHtml =
            issueId != null
                ? `<p class="order-return-drawer__item-reason">${escapeHtml(t().returnReasonLabel || 'Reason')}: ${getIssueLabel(issueId)}</p>`
                : '';
        const statusHtml =
            productStatusId != null
                ? `<p class="order-return-drawer__item-reason">${escapeHtml(t().returnProductStatusLabel || 'Condition')}: ${escapeHtml(getProductStatusLabel(productStatusId))}</p>`
                : '';

        return `
            <div class="order-return-drawer__item-card shipment__item">
                <div class="shipment__item-image-container">${imageHtml}</div>
                <div class="shipment__item-info order-return-drawer__item-info">
                    <p class="shipment__item-name">${escapeHtml(item.itemDescription)}</p>
                    <p class="order-return-drawer__qty-label">${escapeHtml(qtyLabel)}</p>
                    ${reasonHtml}
                    ${statusHtml}
                </div>
            </div>`;
    }

    function showSelectingStep() {
        returnsFlowState.step = 'selecting';
        const stepEl = getReturnsStepEl();
        const shipmentsEl = getShipmentsContainer();
        const footerEl = getReturnsFooterEl();

        if (stepEl) {
            stepEl.innerHTML = '';
            setVisiblyHidden(stepEl, true);
        }
        setVisiblyHidden(shipmentsEl, false);
        if (footerEl) setVisiblyHidden(footerEl, false);
        updateReportContinueButton();
    }

    function renderInlineReturnsPanel({ html, footerHtml = null, centered = false } = {}) {
        const stepEl = getReturnsStepEl();
        const shipmentsEl = getShipmentsContainer();
        const footerEl = getReturnsFooterEl();

        if (!stepEl) return;

        stepEl.className = `order-returns-step${centered ? ' order-returns-step--centered' : ''}`;
        stepEl.innerHTML = `
            <div class="order-return-drawer-root${centered ? ' order-return-drawer-root--centered' : ''}">
                ${html || ''}
                ${footerHtml || ''}
            </div>`;

        setVisiblyHidden(stepEl, false);
        setVisiblyHidden(shipmentsEl, true);
        if (footerEl) setVisiblyHidden(footerEl, true);
    }

    function resetReturnsFlowState() {
        returnsEditModeActive = false;
        returnsFlowState = createReturnsFlowState();
        clearAllReturnValidationErrors();
        setOrderHeaderHidden(false);
        setReturnsEditMode(false);
        showSelectingStep();
    }

    function isAnotherDrawerFlowActive() {
        return Boolean(global.OrderAmendsFlow?.isOrderCancelFlowActive?.());
    }

    function createReturnsApiFailure(name, message) {
        const err = new Error(message || 'Request failed.');
        err.name = name;
        return err;
    }

    function parseReturnsApiResponse(data) {
        if (data?.success === false) {
            throw createReturnsApiFailure('ReturnsApiOuterFailure', 'Request failed.');
        }

        const payload =
            data?.data != null && typeof data.data === 'object' ? data.data : data;

        if (payload?.success === false) {
            const msg = payload.errorMessage || payload.message || '';
            throw createReturnsApiFailure('ReturnsApiInnerFailure', String(msg).trim());
        }

        return payload;
    }

    async function postReturnsApiAction(body) {
        const url = getReturnsApiUrl();
        if (!url) {
            throw new Error(t().returnNotConfigured || 'Returns are not set up yet.');
        }

        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new Error(data.errorMessage || data.message || `HTTP ${res.status}`);
        }
        return parseReturnsApiResponse(data);
    }

    function parseRmaFromResponse(data) {
        if (!data || typeof data !== 'object') return '';
        const candidates = [
            data.generateReturnRequest?.data?.returnAuthNumber,
            data.rmaNumber,
            data.returnRequestNumber,
            data.rma,
            data.RMA,
            data.returnRequestNo,
            data.requestNumber,
            data.returnRequestNum,
        ];
        for (const c of candidates) {
            if (c != null && String(c).trim()) return String(c).trim();
        }
        return '';
    }

    function parseTicketIdFromResponse(data) {
        if (!data || typeof data !== 'object') return '';
        const candidates = [data.ticketId, data.ticketID, data.ticket_id];
        for (const c of candidates) {
            if (c != null && String(c).trim()) return String(c).trim();
        }
        return '';
    }

    function buildGenerateReturnPayload() {
        const creds = getApiCredentials();
        const items = getSelectedReturnItems().map(
            ({ entry, quantity, issueId, productStatusId, itemNumber, grossAmount }) => {
                const item = {
                    lineId: toApiLineId(entry.item.id),
                    quantity,
                    expectedProductState: { id: productStatusId },
                    itemNumber,
                    grossAmount,
                };
                if (issueId != null) {
                    item.itemIssue = { id: issueId };
                }
                return item;
            }
        );
        return {
            action: 'generateReturnRequest',
            platformOrderNum: creds.orderNumber,
            returnType: 'customerReturn',
            items,
            deliveryCharges: 0,
            preferredCourier: { externalId: 1 },
            customerEmail: creds.customerEmail,
            createdBy: buildCreatedBy(creds),
            dontSendToWMS: false,
        };
    }

    function setConfirmReturnEnabled(enabled) {
        const btn = getReturnsStepEl()?.querySelector('[data-oa-return-action="confirm-return"]');
        if (!btn) return;
        btn.disabled = !enabled;
    }

    function getReturnTermsAckEl() {
        return (
            getReturnsStepEl()?.querySelector('[data-return-terms-ack], #return-terms-ack') ||
            null
        );
    }

    function syncConfirmReturnFromTerms() {
        const ackEl = getReturnTermsAckEl();
        setConfirmReturnEnabled(Boolean(ackEl?.checked));
    }

    function handleReturnTermsChange(ev) {
        if (!ev.target?.matches('[data-return-terms-ack]')) return;
        if (returnsFlowState.step !== 'confirm') return;
        syncConfirmReturnFromTerms();
    }

    function initReturnTermsAgreement() {
        const ackEl = getReturnTermsAckEl();
        if (!ackEl) return;

        ackEl.checked = false;
        setConfirmReturnEnabled(false);
    }

    function renderReturnTermsSectionHtml() {
        const tr = t();
        const ackPrefix = decodeHtmlEntities(
            tr.returnTermsConditionsAck || 'I have read and agree to the'
        );
        const policyLabel = decodeHtmlEntities(
            tr.returnTermsConditionsPolicyLabel || 'Returns & Refunds Policy'
        );
        const policyUrl =
            sanitizeHref(tr.returnTermsConditionsPolicyUrl) ||
            'https://www.laura-james.co.uk/pages/returns-policy';

        return `
                <label class="order-return-drawer__terms-ack">
                    <input type="checkbox" id="return-terms-ack" class="order-return-drawer__terms-ack-input" data-return-terms-ack />
                    <span>
                        ${escapeHtml(ackPrefix)}
                        <a href="${escapeHtmlAttr(policyUrl)}" class="order-return-drawer__terms-policy-link" target="_blank" rel="noopener noreferrer">${escapeHtml(policyLabel)}</a>
                    </span>
                </label>`;
    }

    function renderReturnRequestStep() {
        const tr = t();
        const cfg = getReturnsFlowConfig();
        const addressHtml = formatWarehouseAddressHtml(cfg.warehouseAddress);

        const itemsHtml = getSelectedReturnItems()
            .map(({ entry, quantity, issueId, productStatusId }) =>
                renderReturnItemCardHtml(entry.item, {
                    quantity,
                    issueId,
                    productStatusId,
                })
            )
            .join('');

        returnsFlowState.step = 'confirm';
        setOrderHeaderHidden(true);
        renderInlineReturnsPanel({
            html: `
                <h2 class="order-return-drawer__step-title">${escapeHtml(tr.returnSummaryTitle || 'Returns Summary')}</h2>
                <h3 class="order-return-drawer__section-title">${escapeHtml(tr.returnItemsBeingReturned || 'Items Being Returned')}</h3>
                <div class="order-return-drawer__items">${itemsHtml}</div>
                <h3 class="order-return-drawer__section-title">${escapeHtml(tr.returnShippingAddress || 'Shipping Address')}</h3>
                <div class="order-returns__summary-box order-return-drawer__address-box">${addressHtml || escapeHtml('—')}</div>
                <div class="order-returns__summary-box order-return-drawer__note-box">
                    <p>${escapeHtml(tr.returnShippingNote || '')}</p>
                </div>
                ${renderReturnTermsSectionHtml()}`,
            footerHtml: returnFooterHtml([
                { action: 'request-back', label: tr.returnBack || 'Back' },
                {
                    action: 'confirm-return',
                    label: tr.returnConfirmReturn || 'Confirm Return',
                    primary: true,
                    disabled: true,
                },
            ]),
        });
        initReturnTermsAgreement();
    }

    function showReturnLoadingPanel() {
        const tr = t();
        returnsFlowState.step = 'submitting';
        renderInlineReturnsPanel({
            centered: true,
            html: `
                <div class="order-tracker-drawer__loading-message">
                    <div class="order-tracker-drawer__loading-spinner loading-spinner">
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none" class="spinner">
                            <path d="M20.9 10.9492C21.5075 10.9492 22.0057 11.4429 21.945 12.0474C21.7657 13.835 21.1505 15.5574 20.1462 17.0605C18.9375 18.8694 17.2195 20.2793 15.2095 21.1119C13.1995 21.9445 10.9878 22.1623 8.85401 21.7379C6.72022 21.3134 4.7602 20.2658 3.22183 18.7274C1.68345 17.189 0.635799 15.229 0.211362 13.0952C-0.213076 10.9614 0.0047612 8.74969 0.837325 6.7397C1.66989 4.72971 3.07979 3.01175 4.88873 1.80305C6.39177 0.798751 8.11422 0.183524 9.90179 0.00416892C10.5063 -0.0564808 11 0.441705 11 1.04922C11 1.65673 10.5056 2.14213 9.90283 2.21788C8.55114 2.38773 7.25203 2.86986 6.11098 3.63229C4.66383 4.59924 3.53591 5.97361 2.86986 7.5816C2.20381 9.18959 2.02954 10.959 2.36909 12.666C2.70864 14.373 3.54676 15.9411 4.77746 17.1718C6.00816 18.4025 7.57617 19.2406 9.28321 19.5801C10.9902 19.9197 12.7596 19.7454 14.3676 19.0794C15.9756 18.4133 17.35 17.2854 18.3169 15.8382C19.0794 14.6972 19.5615 13.3981 19.7313 12.0464C19.8071 11.4436 20.2925 10.9492 20.9 10.9492Z" fill="#5C5F5C"/>
                        </svg>
                    </div>
                    <p class="order-tracker-drawer__loading-message-text">Processing your request</p>
                </div>
            `,
        });
    }

    function showReturnSuccessPanel(rmaNumber) {
        const tr = t();
        const rma = rmaNumber || returnsFlowState.lastRmaNumber || '';
        const rmaDisplay = rma.startsWith('#') ? rma : `#${rma}`;

        returnsFlowState.step = 'success';
        renderInlineReturnsPanel({
            centered: true,
            html: `
                <div class="order-return-drawer__success-icon" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <circle cx="24" cy="24" r="24" fill="#424442"/>
                        <path d="M14 24.5L21 31.5L34 18.5" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <h2 class="order-return-drawer__success-title">${escapeHtml(tr.returnTicketSubmittedTitle || 'Ticket Submitted')}</h2>
                <p class="order-return-drawer__success-intro">${escapeHtml(tr.returnTicketSubmittedIntro || '')}</p>
                ${rma ? `<p class="order-return-drawer__rma-number">${escapeHtml(rmaDisplay)}</p>` : ''}
                <p class="order-return-drawer__carton-note">${escapeHtml(tr.returnCartonNote || '')}</p>
                <div class="order-return-drawer__hours-box">
                    <p>${tr.returnCallCentreHoursHtml || ''}</p>
                </div>`,
            footerHtml: returnFooterHtml([
                {
                    action: 'refresh-order',
                    label: tr.returnBackToOrderSummary || 'Back to Order Summary',
                    primary: true,
                },
            ]),
        });
    }

    function showReturnNoRmaPanel(ticketId) {
        const tr = t();
        const ticket = ticketId || returnsFlowState.lastTicketId || '';
        const ticketDisplay = ticket ? (String(ticket).startsWith('#') ? ticket : `#${ticket}`) : '';

        returnsFlowState.step = 'success';
        renderInlineReturnsPanel({
            centered: true,
            html: `
                <div class="order-return-drawer__success-icon" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <circle cx="24" cy="24" r="24" fill="#424442"/>
                        <path d="M14 24.5L21 31.5L34 18.5" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <h2 class="order-return-drawer__success-title">${escapeHtml(tr.returnTicketSubmittedTitle || 'Ticket Submitted')}</h2>
                <p class="order-return-drawer__success-intro">${escapeHtml(tr.returnTicketSubmittedNoRmaIntro || '')}</p>
                ${ticketDisplay ? `<p class="order-return-drawer__ticket-label">${escapeHtml(tr.returnTicketNumberLabel || 'Your ticket number is:')}</p>` : ''}
                ${ticketDisplay ? `<p class="order-return-drawer__rma-number">${escapeHtml(ticketDisplay)}</p>` : ''}
                <p class="order-return-drawer__carton-note">${escapeHtml(tr.returnNoRmaSupportNote || '')}</p>`,
            footerHtml: returnFooterHtml([
                {
                    action: 'done',
                    label: tr.returnBackToOrderSummary || 'Back to Order Summary',
                    primary: true,
                },
            ]),
        });
    }

    function showReturnGenericErrorPanel(message) {
        const tr = t();
        const text = (message && String(message).trim()) || tr.returnErrorMessage || '';
        returnsFlowState.step = 'error';
        renderInlineReturnsPanel({
            centered: true,
            html: `<p class="order-amends__retention-message">${escapeHtml(text)}</p>`,
            footerHtml: returnFooterHtml([
                buildContactSupportButton({ primary: true }),
                { action: 'done', label: tr.returnDone || 'Close' },
            ]),
        });
    }

    function handleReturnApiFailure(err) {
        console.error(err);
        if (err?.name === 'ReturnsApiInnerFailure' && err.message) {
            showReturnGenericErrorPanel(err.message);
            return;
        }
        showReturnGenericErrorPanel();
    }

    function setReturnsEditMode(active) {
        returnsEditModeActive = active;
        const root = getRoot();
        if (!root) return;

        root.classList.toggle('order-returns--active-state', active);
        root.classList.toggle('order-returns--read-state', !active);

        const footerEl = getReturnsFooterEl();
        if (footerEl) setVisiblyHidden(footerEl, !active);

        updateReturnsCtaVisibility();

        if (active) {
            updateReportContinueButton();
        }

        if (!active) {
            showSelectingStep();
        }
    }

    function beginReturnFlow() {
        if (isAnotherDrawerFlowActive()) return;
        if (returnsShipmentRegistry.size === 0) return;

        if (global.OrderReportIssueFlow?.isReportIssueFlowActive?.()) {
            global.OrderReportIssueFlow.exitReportIssueMode?.({ clearDraft: false });
        }

        returnsFlowState = createReturnsFlowState();
        initLineSelectionsForAllShipments();
        setReturnsEditMode(true);
        showSelectingStep();
        syncInlineReturnsUi();
    }

    function exitReturnsMode() {
        getOrderTrackerDrawer()?.close();
        resetReturnsFlowState();
    }

    function advanceFromReportDetails() {
        clearAllReturnValidationErrors();
        const result = validateReturnSelections();

        if (!result.hasAnyQty || !result.valid) {
            result.invalidLines.forEach(({ lineKey, missing }) => {
                showLineValidationError(lineKey, buildLineValidationMessage(missing));
            });
            const firstInvalid = result.invalidLines[0]?.lineKey;
            if (firstInvalid) {
                getLineContainer(firstInvalid)?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
            }
            return;
        }

        renderReturnRequestStep();
    }

    async function submitReturnRequest() {
        const payload = buildGenerateReturnPayload();
        if (!payload.items.length || payload.platformOrderNum == null) {
            showReturnGenericErrorPanel();
            return;
        }

        showReturnLoadingPanel();

        try {
            const data = await postReturnsApiAction(payload);
            returnsFlowState.lastRmaNumber = parseRmaFromResponse(data);
            returnsFlowState.lastTicketId = parseTicketIdFromResponse(data);

            if (returnsFlowState.lastRmaNumber) {
                showReturnSuccessPanel(returnsFlowState.lastRmaNumber);
            } else {
                showReturnNoRmaPanel(returnsFlowState.lastTicketId);
            }
        } catch (err) {
            handleReturnApiFailure(err);
        }
    }

    function setLineQuantity(lineKey, nextQty) {
        const entry = returnsItemRegistry.get(lineKey);
        const maxQty = getReturnableQuantity(entry?.item);
        const quantity = Math.max(0, Math.min(maxQty, nextQty));

        const existing = returnsFlowState.lineSelections.get(lineKey) || {
            quantity: 0,
            issueId: null,
            productStatusId: null,
        };

        returnsFlowState.lineSelections.set(lineKey, {
            quantity,
            issueId: quantity > 0 ? existing.issueId : null,
            productStatusId: quantity > 0 ? existing.productStatusId : null,
        });

        syncInlineLineUi(lineKey);
    }

    function activateLine(lineKey) {
        setLineQuantity(lineKey, 1);
    }

    function handleInlineReturnClick(ev) {
        if (!returnsEditModeActive || returnsFlowState.step !== 'selecting') return;

        const root = getRoot();
        if (!root?.contains(ev.target)) return;

        const pickerBtn = ev.target.closest('[data-oa-return-open-drawer]');
        if (pickerBtn) {
            ev.preventDefault();
            const lineKey = pickerBtn.dataset.returnLineKey;
            const pickerType = pickerBtn.dataset.oaReturnOpenDrawer;
            if (!lineKey) return;
            if (pickerType === 'issue') openLineIssueDrawer(lineKey);
            else if (pickerType === 'productStatus') openLineProductStatusDrawer(lineKey);
            return;
        }

        const addBtn = ev.target.closest('[data-oa-return-add]');
        if (addBtn) {
            ev.preventDefault();
            const lineKey = addBtn.dataset.returnLineKey;
            if (lineKey) activateLine(lineKey);
            return;
        }

        const stepBtn = ev.target.closest('[data-oa-return-qty-step]');
        if (stepBtn) {
            if (stepBtn.disabled) return;

            const wrap = stepBtn.closest('[data-oa-return-qty]');
            const lineKey = wrap?.dataset.returnLineKey;
            if (!lineKey || !wrap) return;

            ev.preventDefault();
            const current = parseInt(wrap.querySelector('[data-oa-return-qty-current]')?.textContent, 10) || 0;
            const step = parseInt(stepBtn.dataset.oaReturnQtyStep, 10);
            setLineQuantity(lineKey, current + step);
        }
    }

    function handleReturnAction(action) {
        switch (action) {
            case 'cancel':
            case 'done':
                exitReturnsMode();
                break;
            case 'report-continue':
                if (selectionRequiresSupportRedirect()) {
                    global.location.assign(getReturnsFlowConfig().supportUrl);
                } else {
                    advanceFromReportDetails();
                }
                break;
            case 'request-back':
                setOrderHeaderHidden(false);
                showSelectingStep();
                syncInlineReturnsUi();
                break;
            case 'confirm-return': {
                const ackEl = getReturnTermsAckEl();
                if (!ackEl?.checked) return;
                submitReturnRequest();
                break;
            }
            case 'contact-support':
                break;
            case 'refresh-order':
                global.__orderTrackerRefetchOrder?.();
                exitReturnsMode();
                break;
            default:
                break;
        }
    }

    function handleReturnDrawerAction(action) {
        const drawer = returnsFlowState.drawer;

        switch (action) {
            case 'drawer-cancel':
                closeSelectionDrawer();
                break;
            case 'drawer-continue':
                applyLineDrawerSelection();
                closeSelectionDrawer();
                break;
            case 'report-all-next': {
                returnsFlowState.drawer = {
                    ...drawer,
                    context: 'reportAllProductStatus',
                    reportAllStep: 'productStatus',
                };
                renderSelectionDrawer();
                break;
            }
            case 'report-all-back': {
                returnsFlowState.drawer = {
                    ...drawer,
                    context: 'reportAllIssue',
                    reportAllStep: 'issue',
                    pendingProductStatusId: null,
                };
                renderSelectionDrawer();
                break;
            }
            case 'report-all-continue': {
                if (drawer.pendingProductStatusId == null) return;
                finalizeDrawerOnClose(drawer);
                returnsFlowState.step = 'selecting';
                closeSelectionDrawer();
                break;
            }
            default:
                break;
        }
    }

    function handleDelegatedActionClick(ev) {
        const reportAllBtn = ev.target.closest('.shipment__report-all-btn');
        if (reportAllBtn) {
            ev.preventDefault();
            const batchIndex = parseInt(reportAllBtn.dataset.reportAllBatchIndex, 10);
            if (!Number.isFinite(batchIndex)) return;
            ensureReturnsEditMode();
            openReportAllDrawer(batchIndex);
            return;
        }

        const actionBtn = ev.target.closest('[data-oa-return-action]');
        if (!actionBtn) return;

        if (actionBtn.tagName === 'A' && actionBtn.href) return;

        const drawer = getOrderTrackerDrawer();
        const inDrawer = drawer?.contains(actionBtn);

        if (inDrawer) {
            if (!drawer.classList.contains('order-tracker-drawer--open')) return;
            ev.preventDefault();
            handleReturnDrawerAction(actionBtn.dataset.oaReturnAction);
            return;
        }

        if (!returnsEditModeActive) return;
        if (!getRoot()?.contains(actionBtn)) return;

        ev.preventDefault();
        handleReturnAction(actionBtn.dataset.oaReturnAction);
    }

    function handleReturnDrawerChange(ev) {
        if (returnsFlowState.step !== 'drawer') return;

        const input = ev.target;
        if (input?.type !== 'radio') return;

        const drawer = returnsFlowState.drawer;

        if (input.name === 'return-issue') {
            drawer.pendingIssueId = parseInt(input.value, 10);
            updateDrawerPrimaryButton();
            return;
        }

        if (input.name === 'return-product-status') {
            drawer.pendingProductStatusId = parseInt(input.value, 10);
            updateDrawerPrimaryButton();
        }
    }

    function rebuildReturnsRegistry(orderData) {
        returnsItemRegistry.clear();
        returnsShipmentRegistry.clear();

        const shipments = orderData?.orderDetails?.orderItems?.shipments || [];
        shipments.forEach((shipmentOrBatch, batchIndex) => {
            if (!(shipmentOrBatch.items || []).length) return;

            if (!isShipmentEligibleForReturn(shipmentOrBatch)) return;

            const returnableItems = (shipmentOrBatch.items || [])
                .map((item, itemIndex) => ({ item, itemIndex }))
                .filter(({ item }) => getReturnableQuantity(item) > 0);

            if (!returnableItems.length) return;

            returnsShipmentRegistry.set(batchIndex, { shipmentOrBatch });
            returnableItems.forEach(({ item, itemIndex }) => {
                returnsItemRegistry.set(makeLineKey(batchIndex, itemIndex), {
                    item,
                    batchIndex,
                    itemIndex,
                    shipmentOrBatch,
                });
            });
        });
    }

    function updateReturnsCtaVisibility() {
        const btn = document.getElementById('order-returns-start');
        if (!btn) return;
        btn.classList.toggle('hidden', returnsShipmentRegistry.size === 0 || returnsEditModeActive);
    }

    function initOrderReturns() {
        if (returnsHandlersBound) return;
        returnsHandlersBound = true;

        document.getElementById('order-returns-start')?.addEventListener('click', () => {
            beginReturnFlow();
        });

        document.getElementById('order-returns-cancel-changes')?.addEventListener('click', () => {
            const tr = t();
            if (hasAnyReturnSelections()) {
                const ok = global.confirm(
                    tr.returnDiscardChangesConfirm || 'Discard all return changes?'
                );
                if (!ok) return;
            }
            exitReturnsMode();
        });

        const root = getRoot();
        root?.addEventListener('click', handleInlineReturnClick);
        document.addEventListener('click', handleDelegatedActionClick);
        document.addEventListener('change', handleReturnDrawerChange);
        document.addEventListener('change', handleReturnTermsChange);

        getOrderTrackerDrawer()?.addEventListener('order-tracker-drawer:closed', () => {
            if (returnsFlowState.step === 'drawer') {
                const drawer = { ...returnsFlowState.drawer };
                finalizeDrawerOnClose(drawer);
                resetDrawerState();
                returnsFlowState.step = 'selecting';
            }
        });
    }

    global.OrderReturnsFlow = {
        initOrderReturns,
        rebuildReturnsRegistry,
        updateReturnsCtaVisibility,
        isShipmentEligibleForReturn,
        isLineEligibleForReturn,
        shipmentHasReturnableItems,
        getReturnedQuantityFromReturn,
        getReturnedQuantity,
        getReturnableQuantity,
        makeLineKey,
        isReturnsFlowActive: () => returnsEditModeActive,
        exitReturnsMode,
    };
})(window);
